import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import Header from './components/layout/Header';
import ErrorBoundary from './components/ErrorBoundary';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import PlaybookGallery from './pages/PlaybookGallery';
import PlaybookDetail from './pages/PlaybookDetail';
import CustomFray from './pages/CustomFray';
import ImportPrompt from './pages/ImportPrompt';
import PromptBuilder from './pages/PromptBuilder';
import Gauntlet from './pages/Gauntlet';
import VoiceReveal from './pages/VoiceReveal';
import Theater from './pages/Theater';
import Results from './pages/Results';
import Settings from './pages/Settings';
import AdminPanel from './pages/AdminPanel';
import SharePage from './pages/SharePage';
import Gallery from './pages/Gallery';
import ReplayViewer from './pages/ReplayViewer';
import Workspaces from './pages/Workspaces';
import ContextRepo from './pages/ContextRepo';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Connections from './pages/Connections';
import AuthCallback from './pages/AuthCallback';
import GitHubCallback from './pages/GitHubCallback';
import MicrosoftCallback from './pages/MicrosoftCallback';
import MagicLinkCallback from './pages/MagicLinkCallback';
import TryPage from './pages/TryPage';
import Pricing from './pages/Pricing';
import CatFightGallery from './pages/CatFightGallery';
import CatFightLeaderboard from './pages/CatFightLeaderboard';
import CatFightDetail from './pages/CatFightDetail';
import CatFightLaunch from './pages/CatFightLaunch';
import ClipPage from './pages/ClipPage';
import Voices from './pages/Voices';
import AlteredStates from './pages/AlteredStates';
import TripChat from './pages/TripChat';
import SharedTripChat from './pages/SharedTripChat';
import Community from './pages/Community';
import Leaderboard from './pages/Leaderboard';
import McpSetup from './pages/McpSetup';
import McpOnboarding from './pages/McpOnboarding';
import Onboarding from './pages/Onboarding';
import EnterpriseSettings from './pages/EnterpriseSettings';
import ReferralLanding from './pages/ReferralLanding';
import EnterpriseAdmin from './pages/EnterpriseAdmin';
import SecurityPage from './pages/SecurityPage';
import EnterprisePage from './pages/EnterprisePage';
import SolutionsPage from './pages/SolutionsPage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import InvestorsPage from './pages/InvestorsPage';
import { SitePasswordGate } from './components/PasswordGate';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { ImpersonationProvider, useImpersonation } from './contexts/ImpersonationContext';
import ImpersonationBanner from './components/ImpersonationBanner';
import { attachImpersonationHeader } from './lib/impersonationInterceptor';
import { handleGrantExpired } from './lib/responseInterceptor';

// API setup
const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

// #976: token storage helpers. Access token in `fray_token`, refresh
// token in `fray_refresh_token`. Wrapped so the interceptor, login,
// signup, logout, and OAuth callbacks all share the same lifecycle.
export function saveAuthTokens({ token, refresh_token }) {
  if (token) localStorage.setItem('fray_token', token);
  if (refresh_token) {
    localStorage.setItem('fray_refresh_token', refresh_token);
  } else {
    // Reviewer NIT 3: surface the contract regression loudly in DevTools.
    // If the backend silently stopped returning refresh_token, sessions
    // would quietly end after 1h with no indication why.
    // eslint-disable-next-line no-console
    console.warn('[auth] response missing refresh_token — session will not survive access-token expiry');
  }
}
export function clearAuthTokens() {
  try { localStorage.removeItem('fray_token'); } catch (_) { /* storage unavailable */ }
  try { localStorage.removeItem('fray_refresh_token'); } catch (_) { /* storage unavailable */ }
}

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('fray_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// #1137 Task 3.2: stamp X-Impersonation-Grant onto requests when an
// impersonation grant is active in sessionStorage. Skips the revoke URL
// so the call to end impersonation runs as the actor, not the
// impersonated user (B3 deadlock fix).
API.interceptors.request.use(attachImpersonationHeader);

// #825 Frontend #8: global 401 handler — when the backend rejects a
// request as unauthenticated. Skips paths expected to 401 unauthenticated
// (login probe, OAuth callback) so we don't infinite-redirect.
const _AUTH_PUBLIC_PATHS = ['/api/auth/login', '/api/auth/signup', '/api/auth/me'];

// #976: bounce-to-login helper — shared by the 401 interceptor when
// refresh isn't possible or fails.
function _failOutAndRedirect() {
  clearAuthTokens();
  const here = window.location.pathname;
  if (!here.startsWith('/login') && !here.startsWith('/auth/')) {
    try {
      window.dispatchEvent(new CustomEvent('fray:auth-expired'));
    } catch (_) { /* no-op in non-browser env */ }
    window.location.assign('/login?expired=1');
  }
}

// #976: singleton in-flight refresh promise. Concurrent 401s share one
// /api/auth/refresh call, then all replay with the new access token.
let _refreshInFlight = null;

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    // #1137 Task 3.3: impersonation grant-expired short-circuit.
    // Runs BEFORE the auth-refresh branch — a grant-expiry 401/403 means
    // the actor's grant is dead, NOT their auth token. Refreshing here
    // would burn an attempt and bounce the actor to /login instead of
    // showing the explicit "Impersonation session expired" UX.
    if (handleGrantExpired(error)) {
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const originalRequest = error.config;

    if (status !== 401 || _AUTH_PUBLIC_PATHS.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }

    // Don't loop on the refresh endpoint itself.
    if (url.includes('/api/auth/refresh') || originalRequest?._isRetry) {
      _failOutAndRedirect();
      return Promise.reject(error);
    }

    const rt = localStorage.getItem('fray_refresh_token');
    if (!rt) {
      // No refresh token on hand — user logged in pre-#890 or lost it.
      _failOutAndRedirect();
      return Promise.reject(error);
    }

    try {
      if (!_refreshInFlight) {
        // Use raw axios (bypass `API` instance) so the Authorization
        // header from the request interceptor doesn't re-attach the
        // expired token. Refresh is authenticated by the refresh
        // token in the body, not the Bearer header.
        _refreshInFlight = axios.post(
          `${API.defaults.baseURL || ''}/api/auth/refresh`,
          { refresh_token: rt },
        ).finally(() => { _refreshInFlight = null; });
      }
      const { data } = await _refreshInFlight;
      saveAuthTokens(data);
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      originalRequest._isRetry = true;
      return API(originalRequest);
    } catch {
      _failOutAndRedirect();
      return Promise.reject(error);
    }
  }
);

// Auth context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export { API };

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname.includes('/auth/')) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('fray_token');
    if (token) {
      API.get('/api/auth/me')
        .then(res => setUser(res.data))
        .catch(() => {
          clearAuthTokens();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await API.post('/api/auth/login', { email, password });
    saveAuthTokens(res.data);
    setUser(res.data.user);
    return res.data;
  };

  const signup = async (email, password, name, inviteCode, workspaceInvite) => {
    const res = await API.post('/api/auth/signup', {
      email, password, name, invite_code: inviteCode,
      workspace_invite: workspaceInvite || undefined,
    });
    saveAuthTokens(res.data);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    // #976: best-effort server-side revocation so the refresh token
    // can't be reused if it leaks. Don't block logout on network
    // failure — always clear local state.
    const rt = localStorage.getItem('fray_refresh_token');
    try {
      await API.post('/api/auth/logout', rt ? { refresh_token: rt } : {});
    } catch (_) { /* ignore */ }
    clearAuthTokens();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await API.get('/api/auth/me');
      setUser(res.data);
    } catch {
      // ignore
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ForcePasswordChange() {
  const { user, refreshUser } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = async () => {
    if (newPassword.length < 8) return setError('Password must be at least 8 characters');
    if (newPassword !== confirmPassword) return setError('Passwords do not match');
    setError('');
    setLoading(true);
    try {
      const res = await API.post('/api/auth/change-password', { new_password: newPassword });
      saveAuthTokens(res.data);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-[#0F0F0F]">
      <div className="w-full max-w-sm mx-4 p-6 bg-[#1A1A1A] border border-[#FF4D00]/30">
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#FF4D00]/10 flex items-center justify-center">
            <svg className="w-6 h-6 text-[#FF4D00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold font-['Barlow_Condensed']">Password Change Required</h2>
          <p className="text-xs text-[#A0A0A0] mt-1">Your administrator has required you to set a new password.</p>
        </div>

        {error && <div className="p-2 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs text-center">{error}</div>}

        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
              placeholder="Minimum 8 characters" minLength={8} />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
              placeholder="Type it again" />
          </div>
          <button onClick={handleChange}
            disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
            className="w-full px-4 py-2.5 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50">
            {loading ? 'Updating...' : 'Set New Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Force2FASetup() {
  const navigate = useNavigate();
  return (
    <div className="h-screen flex items-center justify-center bg-[#0F0F0F]">
      <div className="w-full max-w-sm mx-4 p-6 bg-[#1A1A1A] border border-[#FF4D00]/30">
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#FF4D00]/10 flex items-center justify-center">
            <svg className="w-6 h-6 text-[#FF4D00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold font-['Barlow_Condensed']">Two-Factor Authentication Required</h2>
          <p className="text-xs text-[#A0A0A0] mt-1">As a super admin, you must enable two-factor authentication to continue.</p>
        </div>
        <button onClick={() => navigate('/settings?tab=security')}
          className="w-full px-4 py-2.5 bg-[#FF4D00] text-black text-sm font-medium">
          Go to Security Settings
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-[#0F0F0F]"><LoadingSpinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.requires_password_change) return <ForcePasswordChange />;
  // Super admins and enterprise users must have 2FA enabled (#425)
  const requires2FA = (user.role === 'super_admin' || user.role === 'fray_super_admin' || user.plan === 'enterprise');
  if (requires2FA && !user.totp_enabled && !user.requires_password_change) {
    return <Force2FASetup />;
  }
  return <ErrorBoundary scope="route">{children}</ErrorBoundary>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-[#0F0F0F]"><LoadingSpinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const isPlatformAdmin = ['fray_admin', 'fray_super_admin'].includes(user.role);
  if (!isPlatformAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function EnterpriseAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-[#0F0F0F]"><LoadingSpinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const isEntAdmin = ['super_admin', 'admin'].includes(user.role) && user.plan === 'enterprise';
  const isPlatformAdmin = ['fray_admin', 'fray_super_admin'].includes(user.role);
  if (!isEntAdmin && !isPlatformAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function InvestorRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-[#0A0A0A]"><LoadingSpinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_investor && !['fray_admin', 'fray_super_admin'].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function LoadingSpinner() {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
      <span className="text-[#A0A0A0] text-sm font-mono">Sharpening claws...</span>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/github/callback" element={<GitHubCallback />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
      <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
      <Route path="/try" element={<TryPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/mcp-setup" element={<McpOnboarding />} />
      <Route path="/join/referral" element={<ReferralLanding />} />
      <Route path="/clip/:shareToken" element={<ClipPage />} />
      <Route path="/playbooks" element={<PlaybookGallery />} />
      <Route path="/playbooks/:slug" element={<PlaybookDetail />} />
      <Route path="/fray/custom" element={<ProtectedRoute><CustomFray /></ProtectedRoute>} />
      <Route path="/fray/import" element={<ProtectedRoute><ImportPrompt /></ProtectedRoute>} />
      <Route path="/fray/builder" element={<ProtectedRoute><PromptBuilder /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/gauntlet/:templateId" element={<ProtectedRoute><Gauntlet /></ProtectedRoute>} />
      <Route path="/fray/:frayId/reveal" element={<ProtectedRoute><VoiceReveal /></ProtectedRoute>} />
      <Route path="/fray/:frayId" element={<ProtectedRoute><Theater /></ProtectedRoute>} />
      <Route path="/fray/:frayId/results" element={<ProtectedRoute><Results /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/onboarding/:step" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/settings/mcp-setup" element={<ProtectedRoute><McpSetup /></ProtectedRoute>} />
      <Route path="/settings/enterprise" element={<ProtectedRoute><EnterpriseSettings /></ProtectedRoute>} />
      <Route path="/workspaces" element={<ProtectedRoute><Workspaces /></ProtectedRoute>} />
      <Route path="/contexts" element={<ProtectedRoute><ContextRepo /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
      <Route path="/admin/enterprise" element={<EnterpriseAdminRoute><EnterpriseAdmin /></EnterpriseAdminRoute>} />
      <Route path="/share/:shareToken" element={<SharePage />} />
      <Route path="/gallery" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
      <Route path="/cat-fight" element={<ProtectedRoute><CatFightLaunch /></ProtectedRoute>} />
      <Route path="/gallery/cat-fights" element={<CatFightGallery />} />
      <Route path="/gallery/cat-fights/:frayId" element={<CatFightDetail />} />
      <Route path="/leaderboard/cat-fights" element={<CatFightLeaderboard />} />
      <Route path="/voices" element={<ProtectedRoute><Voices /></ProtectedRoute>} />
      <Route path="/altered-states" element={<ProtectedRoute><AlteredStates /></ProtectedRoute>} />
      <Route path="/trip-chat" element={<ProtectedRoute><TripChat /></ProtectedRoute>} />
      <Route path="/trip-chat/shared/:token" element={<SharedTripChat />} />
      <Route path="/community" element={<Community />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/replay/:frayId" element={<ReplayViewer />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/investors" element={<InvestorRoute><InvestorsPage /></InvestorRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// #1137 Task 3.4: AppShell consumes ImpersonationContext so the
// banner can mount + the page wrapper can offset its top padding by
// 40px (banner height) when a grant is active. Lives inside
// ImpersonationProvider so useImpersonation() resolves.
function AppShell({ children }) {
  const ctx = useImpersonation();
  const active = !!ctx?.impersonation;
  return (
    <>
      <ImpersonationBanner />
      <div
        className={`min-h-screen bg-[#0A0A0A] text-[#F5F1E8] ${active ? 'pt-10' : ''}`}
      >
        {children}
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SitePasswordGate>
        <AuthProvider>
          <ImpersonationProvider>
            <ToastProvider>
              <ConfirmProvider>
                <AppShell>
                  <ErrorBoundary scope="app">
                    <AppRoutes />
                  </ErrorBoundary>
                </AppShell>
              </ConfirmProvider>
            </ToastProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </SitePasswordGate>
    </BrowserRouter>
  );
}
